import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X, Phone, CaretDown } from '@phosphor-icons/react';
import { BRAND } from '../lib/brand';
import { SERVICES_INDEX } from '../lib/servicesIndex';
import { COUNTIES } from '../lib/serviceAreas';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAreasOpen, setMobileAreasOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isHomeActive = location.pathname === '/';
  const isServicesActive = location.pathname.startsWith('/services');
  const isAreasActive =
    location.pathname.startsWith('/areas') || location.pathname === '/areas-we-serve';

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setMobileAreasOpen(false);
  };

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#800000] shadow-lg border-b border-[#D4AF37]/30"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo + brand */}
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 flex-shrink-0 group"
          >
            <div className="bg-white p-1 rounded-full">
              <img
                src={BRAND.logoUrl}
                alt="India's Inking Service logo"
                className="h-10 w-10 rounded-full object-cover"
              />
            </div>
            <div className="hidden sm:block leading-tight">
              <p className="font-serif-display text-xl font-semibold text-white">
                India's Inking Service
              </p>
              <p className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37]">
                Nashville • MBA-Led
              </p>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-7">
            {/* Home */}
            <Link
              to="/"
              data-testid="header-nav-home"
              className={`font-medium text-sm tracking-wide uppercase transition-colors relative ${
                isHomeActive ? 'text-[#D4AF37]' : 'text-white/90 hover:text-[#D4AF37]'
              }`}
            >
              Home
              {isHomeActive && (
                <span className="absolute -bottom-1.5 left-0 right-0 h-px bg-[#D4AF37]" />
              )}
            </Link>

            {/* About */}
            <Link
              to="/about"
              data-testid="header-nav-about"
              className={`font-medium text-sm tracking-wide uppercase transition-colors relative ${
                isActive('/about') ? 'text-[#D4AF37]' : 'text-white/90 hover:text-[#D4AF37]'
              }`}
            >
              About
              {isActive('/about') && (
                <span className="absolute -bottom-1.5 left-0 right-0 h-px bg-[#D4AF37]" />
              )}
            </Link>

            {/* Services dropdown — now linked */}
            <div className="relative group">
              <Link
                to="/services"
                data-testid="header-nav-services"
                className={`flex items-center gap-1 font-medium text-sm tracking-wide uppercase transition-colors relative ${
                  isServicesActive
                    ? 'text-[#D4AF37]'
                    : 'text-white/90 hover:text-[#D4AF37]'
                }`}
              >
                Services
                <CaretDown
                  weight="bold"
                  size={12}
                  className="transition-transform group-hover:rotate-180"
                />
                {isServicesActive && (
                  <span className="absolute -bottom-1.5 left-0 right-[18px] h-px bg-[#D4AF37]" />
                )}
              </Link>

              {/* Dropdown panel */}
              <div
                data-testid="services-dropdown-panel"
                className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 absolute top-full left-1/2 -translate-x-1/2 mt-3 w-80 bg-white shadow-2xl border-t-2 border-[#D4AF37] rounded-sm py-3 transition-all duration-200 z-50"
              >
                <div className="px-5 pb-3 mb-2 border-b border-[#E8E2D5]">
                  <p className="font-serif-display text-sm text-[#800000] italic">
                    What we handle —
                  </p>
                </div>
                <ul className="max-h-[420px] overflow-y-auto">
                  {SERVICES_INDEX.map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/services/${s.slug}`}
                        data-testid={`services-dropdown-link-${s.slug}`}
                        className={`block px-5 py-2 text-sm transition-colors ${
                          location.pathname === `/services/${s.slug}`
                            ? 'bg-[#FBF8F1] text-[#800000] font-semibold'
                            : 'text-[#1A1A1A] hover:bg-[#FBF8F1] hover:text-[#800000]'
                        }`}
                      >
                        {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="px-5 pt-3 mt-2 border-t border-[#E8E2D5]">
                  <Link
                    to="/services"
                    data-testid="services-dropdown-view-all"
                    className="inline-flex items-center gap-1 text-xs font-semibold tracking-[0.15em] uppercase text-[#800000] hover:text-[#D4AF37] transition-colors"
                  >
                    View all services →
                  </Link>
                </div>
              </div>
            </div>

            {/* Service Areas dropdown */}
            <div className="relative group">
              <Link
                to="/areas-we-serve"
                data-testid="header-nav-areas"
                className={`flex items-center gap-1 font-medium text-sm tracking-wide uppercase transition-colors relative ${
                  isAreasActive
                    ? 'text-[#D4AF37]'
                    : 'text-white/90 hover:text-[#D4AF37]'
                }`}
              >
                Areas
                <CaretDown
                  weight="bold"
                  size={12}
                  className="transition-transform group-hover:rotate-180"
                />
                {isAreasActive && (
                  <span className="absolute -bottom-1.5 left-0 right-[18px] h-px bg-[#D4AF37]" />
                )}
              </Link>

              <div
                data-testid="areas-dropdown-panel"
                className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 absolute top-full right-0 mt-3 w-72 bg-white shadow-2xl border-t-2 border-[#D4AF37] rounded-sm py-3 transition-all duration-200 z-50"
              >
                <div className="px-5 pb-3 mb-2 border-b border-[#E8E2D5]">
                  <p className="font-serif-display text-sm text-[#800000] italic">
                    Counties we cover —
                  </p>
                </div>
                <ul>
                  {COUNTIES.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/areas/county/${c.slug}`}
                        data-testid={`areas-dropdown-link-${c.slug}`}
                        className={`flex items-center justify-between px-5 py-2 text-sm transition-colors ${
                          location.pathname === `/areas/county/${c.slug}`
                            ? 'bg-[#FBF8F1] text-[#800000] font-semibold'
                            : 'text-[#1A1A1A] hover:bg-[#FBF8F1] hover:text-[#800000]'
                        }`}
                      >
                        <span>{c.name}</span>
                        <span className="text-[10px] text-[#5C5C5C] tracking-wide uppercase">
                          {c.seat}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="px-5 pt-3 mt-2 border-t border-[#E8E2D5]">
                  <Link
                    to="/areas-we-serve"
                    data-testid="areas-dropdown-view-all"
                    className="inline-flex items-center gap-1 text-xs font-semibold tracking-[0.15em] uppercase text-[#800000] hover:text-[#D4AF37] transition-colors"
                  >
                    All counties &amp; cities →
                  </Link>
                </div>
              </div>
            </div>

            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="header-call-link"
              className="flex items-center gap-2 text-sm font-medium text-white/90 hover:text-[#D4AF37] transition-colors"
            >
              <Phone weight="fill" size={16} />
              <span className="hidden lg:inline">{BRAND.phone}</span>
            </a>

            <Link
              to="/booking"
              data-testid="header-book-now-btn"
              className="bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-6 py-2.5 rounded-sm font-semibold text-sm tracking-wide uppercase shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={28} /> : <List size={28} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden py-6 border-t border-[#D4AF37]/30 bg-[#800000] animate-fade-in"
          >
            <div className="flex flex-col gap-1">
              {/* Mobile Home */}
              <Link
                to="/"
                data-testid="mobile-nav-home"
                onClick={closeMobile}
                className={`py-3 px-2 font-medium text-base border-b border-[#D4AF37]/15 ${
                  isHomeActive ? 'text-[#D4AF37]' : 'text-white'
                }`}
              >
                Home
              </Link>

              <Link
                to="/about"
                data-testid="mobile-nav-about"
                onClick={closeMobile}
                className={`py-3 px-2 font-medium text-base border-b border-[#D4AF37]/15 ${
                  isActive('/about') ? 'text-[#D4AF37]' : 'text-white'
                }`}
              >
                About
              </Link>

              {/* Mobile services accordion */}
              <div className="border-b border-[#D4AF37]/15">
                <div className="flex items-center">
                  <Link
                    to="/services"
                    data-testid="mobile-nav-services"
                    onClick={closeMobile}
                    className={`flex-1 py-3 px-2 font-medium text-base ${
                      isServicesActive ? 'text-[#D4AF37]' : 'text-white'
                    }`}
                  >
                    Services
                  </Link>
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    data-testid="mobile-nav-services-toggle"
                    aria-label="Toggle services list"
                    className="py-3 px-3 text-white"
                  >
                    <CaretDown
                      weight="bold"
                      size={14}
                      className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>
                {mobileServicesOpen && (
                  <ul className="pb-3 pl-4">
                    {SERVICES_INDEX.map((s) => (
                      <li key={s.slug}>
                        <Link
                          to={`/services/${s.slug}`}
                          onClick={closeMobile}
                          className="block py-2 px-2 text-sm text-white/85 hover:text-[#D4AF37]"
                        >
                          · {s.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Mobile areas accordion */}
              <div className="border-b border-[#D4AF37]/15">
                <div className="flex items-center">
                  <Link
                    to="/areas-we-serve"
                    data-testid="mobile-nav-areas"
                    onClick={closeMobile}
                    className={`flex-1 py-3 px-2 font-medium text-base ${
                      isAreasActive ? 'text-[#D4AF37]' : 'text-white'
                    }`}
                  >
                    Areas
                  </Link>
                  <button
                    type="button"
                    onClick={() => setMobileAreasOpen(!mobileAreasOpen)}
                    data-testid="mobile-nav-areas-toggle"
                    aria-label="Toggle areas list"
                    className="py-3 px-3 text-white"
                  >
                    <CaretDown
                      weight="bold"
                      size={14}
                      className={`transition-transform ${mobileAreasOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>
                {mobileAreasOpen && (
                  <ul className="pb-3 pl-4">
                    {COUNTIES.map((c) => (
                      <li key={c.slug}>
                        <Link
                          to={`/areas/county/${c.slug}`}
                          onClick={closeMobile}
                          className="block py-2 px-2 text-sm text-white/85 hover:text-[#D4AF37]"
                        >
                          · {c.name}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link
                        to="/areas-we-serve"
                        onClick={closeMobile}
                        className="block py-2 px-2 text-sm text-[#D4AF37] font-semibold"
                      >
                        · All counties &amp; cities →
                      </Link>
                    </li>
                  </ul>
                )}
              </div>

              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="mobile-nav-call"
                onClick={closeMobile}
                className="flex items-center gap-2 py-3 px-2 text-white/85 border-b border-[#D4AF37]/15"
              >
                <Phone weight="fill" size={18} />
                {BRAND.phone}
              </a>
              <Link
                to="/booking"
                data-testid="mobile-nav-book-now"
                onClick={closeMobile}
                className="mt-4 bg-[#D4AF37] text-[#1A1A1A] text-center px-6 py-3 rounded-sm font-semibold uppercase tracking-wide hover:bg-[#F5E6A8] transition-colors"
              >
                Book Your Appointment
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
